import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import OfferModal from "@/components/OfferModal";
import SmartLink from "@/components/SmartLink";
import { auth, db } from "@/lib/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import type { UserPublicProfile } from "@/types/user";
import { buildLgsSearchUrl } from "@/lib/lgs";

export type OfferButtonCardContext = {
  cardId?: string;
  cardOwnerUid?: string;
  name?: string;
  scryfall_id?: string;
};

type Props = {
  toUid: string;
  toUsername?: string;
  card?: OfferButtonCardContext;
  children?: React.ReactNode;
  variant?: "default" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  className?: string;
  disabled?: boolean;
  onOpenOffer?: () => void; // optional: let caller open its own modal
};

// Safe analytics: dynamic import avoids require() in ESM builds
async function logAnalyticsEvent(name: string, params?: Record<string, any>) {
  try {
    const mod = await import("firebase/analytics");
    if (typeof mod.isSupported === "function") {
      const supported = await mod.isSupported();
      if (!supported) return;
    }
    const ga = mod.getAnalytics();
    mod.logEvent(ga, name, params || {});
  } catch {
    // analytics not configured or unsupported; ignore
  }
}

function isValidExternalUrl(url?: string | null) {
  if (!url) return false;
  if (!/^https?:\/\//i.test(url)) return false;
  if (url.includes("{q}")) return false;
  return true;
}

export default function OfferButton({
  toUid,
  toUsername,
  card,
  children = "Offer",
  variant = "outline",
  size = "sm",
  className,
  disabled,
  onOpenOffer,
}: Props) {
  // In-app modal state (retained for backward compatibility)
  const [open, setOpen] = useState(false);
  const myUid = auth.currentUser?.uid ?? null;

  // Load owner profile to check LGS routing
  const [owner, setOwner] = useState<UserPublicProfile | null>(null);
  useEffect(() => {
    if (!toUid) {
      setOwner(null);
      return;
    }
    const ref = doc(db, "users", toUid);
    return onSnapshot(ref, (snap) => {
      setOwner(snap.exists() ? ({ uid: toUid, ...(snap.data() as any) } as UserPublicProfile) : null);
    });
  }, [toUid]);

  const cardName = card?.name || "";

  // External offer link for LGS website mode
  let externalHref: string | null = null;
  if (owner?.isLGS && owner?.lgs?.offerContact === "website" && owner?.lgs) {
    const href = buildLgsSearchUrl(owner.lgs, cardName);
    if (isValidExternalUrl(href)) {
      externalHref = href;
    } else {
      void logAnalyticsEvent("lgs_search_url_invalid", {
        lgsUid: owner.uid,
        cardName: cardName || null,
      });
    }
  }

  if (externalHref) {
    return (
      <SmartLink
        to={externalHref}
        className={className ?? "btn btn-primary"}
        target="_blank"
        rel="noopener"
        onClick={() => {
          void logAnalyticsEvent("lgs_offer_click_external", {
            lgsUid: owner?.uid || toUid,
            cardName: cardName || null,
          });
        }}
        data-variant={variant}
        data-size={size}
        aria-disabled={disabled ? "true" : undefined}
      >
        {children ?? "Offer"}
      </SmartLink>
    );
  }

  // Internal flow: use caller's modal when provided; otherwise open built-in OfferModal
  const disabledReason = disabled || !toUid || !myUid || toUid === myUid;

  return (
    <>
      <Button
        type="button"
        variant={variant as any}
        className={className}
        size={size as any}
        disabled={!!disabledReason}
        onClick={() => {
          if (owner?.isLGS && owner?.lgs?.offerContact !== "website") {
            void logAnalyticsEvent("lgs_offer_internal_start", {
              lgsUid: owner?.uid || toUid,
              cardName: cardName || null,
            });
          }
          if (onOpenOffer) {
            onOpenOffer();
          } else {
            setOpen(true);
          }
        }}
        data-variant={variant}
        data-size={size}
        aria-haspopup="dialog"
      >
        {children ?? "Offer"}
      </Button>

      {!onOpenOffer && open && (
        <OfferModal
          open={open}
          onClose={() => setOpen(false)}
          toUid={toUid}
          toUsername={toUsername}
          card={card}
        />
      )}
    </>
  );
}
